import React, { useState, useEffect, useRef } from 'react';
import Box from "@mui/material/Box";
import Typography from '@mui/material/Typography';
import { NotificationManager } from "react-notifications";
import resetPasswordOtpResponse from '../../apis/password/resetPasswordOtp';
import resetPasswordVerifyResponse from '../../apis/password/resetPasswordVerify';
import { millisToMinutesAndSeconds } from '../../utilities/validations';
import './otp.css';

const OTP = ({ 
  otp = '', 
  onChange, 
  length = 6, 
  label = 'OTP:', 
  className = 'otp-box',
  wrapperClassName = 'otp-box-wrapper',
  labelClassName = 'auth-label',
  disabled = false,
  autoFocus = false,
  verificationMode = false,
  email = '',
  onOtpVerified = null,
  showResendButton = true,
  initialCountdown = 300000
}) => {
  const [counter, setCounter] = useState(initialCountdown);
  const [isActive, setIsActive] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const showResend = counter === 0 || !isActive;
  const inputRefs = useRef([]);

  useEffect(() => {
    if (verificationMode) {
      setIsActive(true);
    }
  }, [verificationMode]);

  useEffect(() => {
    let intervalId;

    if (isActive && counter > 0) {
      intervalId = setInterval(() => {
        setCounter((prevCounter) => prevCounter - 1000);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isActive, counter]);
  
  const handleChange = (e, index) => {
    const val = e.target.value;
    
    if (val.length > 1) return;
    
    const otpArr = (otp || "").split("");
    
    while (otpArr.length < length) {
      otpArr.push("");
    }
    
    otpArr[index] = val;
    const newOtp = otpArr.join("");
    

    if (onChange) {
      onChange(newOtp);
    }
    
    if (val && index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp?.[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, length);
    if (onChange) {
      onChange(pastedData);
    }
  };

  const handleVerifyOtp = async () => {
    if (!verificationMode || !email) return;
    
    const payloadData = { email, otp };
    setIsVerifying(true);
    
    try {
      const resetpasswordverifyData = await resetPasswordVerifyResponse(payloadData);

      if (resetpasswordverifyData.data.message.includes('Invalid OTP')) {
        NotificationManager.error("Please enter valid OTP", "Error", 10000);
        if (onChange) {
          onChange('');
        }
        return;
      }
      
      NotificationManager.success("OTP verified", "Success", 10000);
      setCounter(initialCountdown);
      setIsActive(false);
      
      if (onOtpVerified) {
        onOtpVerified();
      }
    } catch (error) {
      console.log(error);
      if (onChange) {
        onChange('');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!verificationMode || !email) return;
    
    const payloadData = { email };
    setIsResending(true);
    
    try {
      const resetpassworotpData = await resetPasswordOtpResponse(payloadData);
      
      if (resetpassworotpData.data.message.includes('We do not have an account in ClearedTalent')) {
        NotificationManager.error("We do not have an account in ClearedTalent with this email address", "Error", 10000);
        return;
      }
      
      NotificationManager.success("OTP Sent", "Success", 10000);
      setCounter(initialCountdown);
      setIsActive(true);
      if (onChange) {
        onChange('');
      }
      
      // Focus first input after resend
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsResending(false);
    }
  };

  if (verificationMode) {
    return (
      <div className="login_wrapper_main">
        <div className='login_container login-box-border reset-password-modern'>
          <div className='candidate-signin-title'>
            <p className='fs-semi-bold fs-22'>Reset your password</p>
          </div>
          
          <div>
            <label className="field_name email-label-form-one" htmlFor="email">
              Email:
            </label>
            
            <div className='fp-email-wrapper'>
              <div className='fp-email-with-err'>
                <input
                  type="text"
                  id="email"
                  className="mb_20 ht_47 emailStyle"
                  value={email}
                  disabled
                />
              </div>
              
              {showResendButton && (
                <div className='formone-otpbtnswitch'>
                  {counter > 0 ? (
                    <button type="button" disabled>
                      {millisToMinutesAndSeconds(counter)}
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      onClick={handleResendOtp}
                      disabled={isResending}
                    >
                      {isResending ? 'Sending...' : 'Resend OTP'}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt_34">
              <div className='resetPwdOTPText'>
                <label htmlFor="otp-verification-0"><b>OTP:</b></label>
              </div>
              
              <div className="otp-verification-wrapper">
                <div className="otp-box-wrapper-verification">
                  {[...Array(length)].map((_, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      maxLength="1"
                      value={otp[index] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.match(/^[0-9]$/) || value === '') {
                          const newOtp = otp.split('');
                          newOtp[index] = value;
                          onChange && onChange(newOtp.join(''));
                          
                          if (value && index < length - 1 && inputRefs.current[index + 1]) {
                            inputRefs.current[index + 1].focus();
                          }
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace') {
                          const newOtp = otp.split('');
                          if (newOtp[index]) {
                            newOtp[index] = '';
                            onChange && onChange(newOtp.join(''));
                          } else if (index > 0 && inputRefs.current[index - 1]) {
                            newOtp[index - 1] = '';
                            onChange && onChange(newOtp.join(''));
                            inputRefs.current[index - 1].focus();
                          }
                        }
                      }}
                      onFocus={(e) => e.target.select()}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
                
                <div className="verify-otp-btn-wrapper">
                  <button
                    type="button"
                    className="btn-reset-password-modern"
                    onClick={handleVerifyOtp}
                    disabled={!otp || otp.length < length || isVerifying}
                  >
                    {isVerifying ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render standard OTP input mode
  return (
    <div>
      {label && <Typography className={labelClassName}>{label}</Typography>}
      <Box className={wrapperClassName}>
        {[...Array(length)].map((_, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            maxLength={1}
            className={className}
            value={otp?.[i] || ''}
            onChange={(e) => handleChange(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onPaste={handlePaste}
            disabled={disabled}
            autoFocus={autoFocus && i === 0}
          />
        ))}
      </Box>
    </div>
  );
};

export default OTP;

